import { Redirect, Route, Router, Switch } from "react-router-dom";
import type { FC } from "react";
import { lazy, memo, Suspense, useState } from "react";
import type { History } from "history";
import { getIsWeb } from "./@api/config";
import { TopBar } from "@kivra/react-components";
import type { KnownLanguage } from "@kivra/sdk/types";
import { routes } from "./utils/routes";
import { appRouteArray } from "./utils/routesConfig";
import type { Config } from "./types";
import { CopyContextProvider } from "./utils/globalContext";
import { Layout } from "./components/layouts/Layout";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import type { Copy } from "./copy";
import { LanguageSelector } from "./components/language/LanguageSelector";
import { getHideNavigation } from "./store/hideNavigation/hideNavigation";
import { SessionEndedError } from "./components/atoms/SessionEndedError";
import { getSessionError } from "./store/sessionError";
import type { LangResponse } from "./@api/langJson";
import type { LegitimateAny } from "@kivra/sdk/types/util/any";

const TemporaryServiceMessage = lazy(
  () => import("./components/TemporaryServiceMessage"),
);

interface AppProps {
  history: History;
  config: Config;
  lang: LangResponse;
  initLang: KnownLanguage<"fi">;
}

export const App: FC<AppProps> = memo(
  ({ history, config, lang, initLang }: AppProps) => {
    const [copy, setCopy] = useCopyFromLang(lang, initLang);
    const isMobile = getHideNavigation() || !getIsWeb();
    const sessionError = getSessionError();

    return (
      <Router history={history}>
        <CopyContextProvider value={{ copy, setCopy }}>
          {!isMobile && (
            <TopBar
              alignLogo="left"
              endAdornment={<LanguageSelector />}
              logoHref={""}
            />
          )}
          <Layout>
            {!isMobile && (
              <>
                <ErrorBoundary>
                  <Suspense fallback={null}>
                    <TemporaryServiceMessage
                      copy={copy}
                      config={config}
                    />
                  </Suspense>
                </ErrorBoundary>
                {sessionError && <SessionEndedError />}
              </>
            )}
            <Switch>
              {appRouteArray.map(
                ({ appExact, path, appComponent }, key) => (
                  <Route
                    exact={appExact}
                    path={path}
                    component={appComponent as LegitimateAny}
                    key={key}
                  />
                ),
              )}
              <Redirect to={routes.ROOT} />
            </Switch>
          </Layout>
        </CopyContextProvider>
      </Router>
    );
  },
);

const useCopyFromLang = (
  lang: LangResponse,
  initLang: KnownLanguage<"fi">,
): [Copy, React.Dispatch<React.SetStateAction<KnownLanguage<"fi">>>] => {
  const [copy, setCopy] = useState<KnownLanguage<"fi">>(initLang);
  return [lang[copy], setCopy];
};
